import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Target, Briefcase, Cpu, ArrowRight, ExternalLink } from 'lucide-react';
import Banner1 from '../assets/img/banner/banner_5.jpg';
import WelcomeImg from '../assets/img/carousel/email-icon.png';
import ServiceHighlights from '../components/ServiceHighlights';
import { ContactForm } from '../components/ContactForm';
import { PROJECTS } from '../constants/projects';
import contactInfo from '../constants/contactInfo.json';

const Home: React.FC = () => {
  // Show only the first 3 projects on the home page for a clean look
  const featuredProjects = PROJECTS.slice(0, 3);

  return (
    <div className="home-page">
      {/* ... (Hero Slider Section same as before) ... */}
      <section className="hero">
        <div className="shell hero-grid">
          <div className="hero-copy fade-in-up">
            <div className="badge-pill">Leading IT Solutions Provider</div>
            <h1 className="hero-title">
              Responsive Web <span className="text-gradient">Applications</span>
            </h1>
            <p className="lead">
              At OneSoft, we specialize in high-level software development and IT consulting
              to help your business grow and succeed in the digital age.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="primary-btn">Start Project</Link>
              <Link to="/services" className="ghost-btn">Our Services</Link>
            </div>

            <div className="stats-row">
              <div className="stat-item">
                <strong>15+</strong><span>Years Exp</span>
              </div>
              <div className="stat-separator"></div>
              <div className="stat-item">
                <strong>500+</strong><span>Clients</span>
              </div>
              <div className="stat-separator"></div>
              <div className="stat-item">
                <strong>24/7</strong><span>Support</span>
              </div>
            </div>
          </div>

          <div className="hero-visual fade-in-up delay-1">
            <div className="glass-card" style={{ padding: '0', overflow: 'hidden', border: 'none' }}>
              <img src={Banner1} alt="Banner" style={{ width: '100%', height: 'auto', display: 'block' }} />
              <div className="carousel-caption" style={{ position: 'absolute', bottom: '0', background: 'rgba(0,0,0,0.5)', width: '100%', padding: '20px', color: 'white' }}>
                <p>Advanced CSS3 Transitions & Responsive Design</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceHighlights />

      {/* Service Blocks */}
      <section className="grid-section" id="overview" style={{ padding: '80px 0' }}>
        <div className="shell">
          <div className="grid-layout">
            <article className="feature-card fade-in-up">
              <div className="icon-box"><Target size={24} /></div>
              <h3>Our Mission</h3>
              <p>To enable people and companies to increase their business value through the use of IT technologies. We focus on secure, scalable and reliable systems.</p>
              <Link to="/about/mission" className="text-link">Read more <ArrowRight size={14} /></Link>
            </article>
            <article className="feature-card highlight fade-in-up delay-1">
              <div className="icon-box"><Briefcase size={24} /></div>
              <h3>Our Services</h3>
              <p>Committed to delivering innovation, OneSoft collaborates with clients to provide high quality solutions at competitive prices.</p>
              <Link to="/services" className="text-link">Read more <ArrowRight size={14} /></Link>
            </article>
            <article className="feature-card fade-in-up delay-2">
              <div className="icon-box"><Cpu size={24} /></div>
              <h3>Methodology</h3>
              <p>Being customer-oriented, we make our processes clear and transparent. Our methodology ensures project delivery on time and within budget.</p>
              <Link to="/methodology" className="text-link">Read more <ArrowRight size={14} /></Link>
            </article>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="media-section" style={{ background: 'var(--bg-elevated)', padding: '100px 0' }}>
        <div className="shell">
          <div className="hero-grid">
            <div className="hero-visual fade-in-up">
              <img src={WelcomeImg} alt="Welcome" className="img-width-200" style={{ maxWidth: '300px' }} />
            </div>
            <div className="hero-copy fade-in-up delay-1">
              <h2 className="section-title">Welcome To <span className="text-gradient">ONESOFT Systems</span></h2>
              <p>
                OneSoft is a privately owned company focusing on providing IT consulting services,
                offshore web development, and software development. We offer re-engineering solutions
                and ready-to-deploy business applications for industries including e-commerce, banking,
                medicine, healthcare, aviation, and manufacturing.
              </p>
              <ul className="unstyled-list" style={{ listStyle: 'none', padding: '0' }}>
                <li style={{ marginBottom: '10px' }}><span style={{ color: 'var(--accent)', marginRight: '10px' }}>✓</span> Application Support</li>
                <li style={{ marginBottom: '10px' }}><span style={{ color: 'var(--accent)', marginRight: '10px' }}>✓</span> Business Analysis</li>
                <li style={{ marginBottom: '10px' }}><span style={{ color: 'var(--accent)', marginRight: '10px' }}>✓</span> Outsourcing Solutions</li>
              </ul>
              <Link to="/about" className="primary-btn" style={{ marginTop: '20px', alignSelf: 'flex-start' }}>Learn More About Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Portfolio */}
      <section className="portfolio-section" style={{ padding: '100px 0' }}>
        <div className="shell">
          <h2 className="section-title fade-in" style={{ textAlign: 'center' }}>Key <span className="text-gradient">Solutions</span></h2>
          <p className="section-subtitle fade-in" style={{ textAlign: 'center' }}>Empowering industries with cutting-edge technology</p>

          <div className="portfolio-grid" style={{ marginTop: '50px' }}>
            {featuredProjects.map((project, index) => (
              <article key={project.id} className={`portfolio-item fade-in-up ${index === 1 ? 'delay-1' : index === 2 ? 'delay-2' : ''}`}>
                <div className="portfolio-image" style={project.gradient ? { background: project.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
                  {project.image ? (
                    <img src={project.image} alt={project.title} style={{ width: '100%', height: '100%', objectFit: project.imageType || 'cover', padding: project.imageType === 'contain' ? '24px' : '0' }} />
                  ) : (
                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: '24px' }}>{project.id.toUpperCase().replace('-', ' ')}</span>
                  )}
                </div>
                <div className="portfolio-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  {project.link && (
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-gradient" style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      View Project <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '60px' }}>
            <Link to="/projects" className="primary-btn large">View All Projects</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" id="contact" style={{ padding: '80px 0' }}>
        <div className="shell">
          <div className="cta-card fade-in-up" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center', textAlign: 'left' }}>
            <div>
              <h2>Ready to transform your business?</h2>
              <p style={{ marginBottom: '30px' }}>Get in touch with our experts today for a free consultation. We usually respond within 24 hours.</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="icon-wrapper" style={{ width: '32px', height: '32px' }}><Mail size={16} /></div>
                  <a href={`mailto:${contactInfo.email}`} style={{ color: 'inherit' }}>{contactInfo.email}</a>
                </div>
                {contactInfo.phones.map((phone) => (
                  <div key={phone} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="icon-wrapper" style={{ width: '32px', height: '32px' }}><Phone size={16} /></div>
                    <a href={`tel:${phone}`} style={{ color: 'inherit' }}>{phone}</a>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card" style={{ padding: '30px', background: 'rgba(255,255,255,0.05)' }}>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
